import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { Button } from './button';
import { FileText, TrendingUp, Trophy, TrendingDown, Sparkles } from 'lucide-react';

// 로컬화 사전 (MemberInsights 페이지와 동일)
const CATEGORY_KO = {
  strength: '근력',
  muscular_endurance: '근지구력',
  endurance: '근지구력',
  cardio: '심폐지구력',
  inbody: '체성분',
  body_composition: '체성분',
  posture: '체형',
  body_balance: '체형',
};
const EXERCISE_KO = {
  squat: '스쿼트',
  deadlift: '데드리프트',
  bench_press: '벤치프레스',
  shoulder_press: '숄더프레스',
  pushup: '푸시업',
  push_up: '푸시업',
  situp: '싯업',
  sit_up: '싯업',
  plank: '플랭크',
  wall_squat: '월스쿼트',
  running: '러닝',
  rowing: '로잉',
  amrap1: 'AMRAP1',
  amrap2: 'AMRAP2',
  amrap3: 'AMRAP3',
  amrap4: 'AMRAP4',
  posture: '체형',
  strength: '근력',
  cardio: '심폐지구력',
};
const koCat = (s) => (s ? CATEGORY_KO[s] || s : '');
const koEx = (s) => (s ? EXERCISE_KO[s] || s : '');

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];
const fmtKstDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const y = kst.getUTCFullYear();
  const m = String(kst.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(kst.getUTCDate()).padStart(2, '0');
  const w = WEEKDAY_KO[kst.getUTCDay()];
  return `${y}.${m}.${dd} (${w})`;
};
const sign = (n) => {
  if (n == null) return { text: '', className: '' };
  const num = Number(n);
  if (Number.isNaN(num)) return { text: '', className: '' };
  const rounded = Math.round(num * 10) / 10;
  if (rounded > 0) return { text: `(+${rounded}점)`, className: 'text-green-600 font-medium' };
  if (rounded < 0) return { text: `(${rounded}점)`, className: 'text-red-600 font-medium' };
  return { text: '(0점)', className: 'text-gray-500' };
};
const rawMetrics = (m) => {
  if (!m || typeof m !== 'object') return '';
  const parts = [];
  if (m.weight != null && m.reps != null) parts.push(`${m.weight}kg × ${m.reps}`);
  else if (m.weight != null) parts.push(`${m.weight}kg`);
  else if (m.reps != null) parts.push(`${m.reps}회`);
  if (m.duration_s != null) parts.push(`${m.duration_s}초`);
  if (m.distance_m != null) parts.push(`${m.distance_m}m`);
  return parts.join(' ');
};

const HEADLINE_ICON = {
  FIRST_PENTAGON: Sparkles,
  CATEGORY_LEVEL_UP: Trophy,
  CATEGORY_LEVEL_DOWN: TrendingDown,
  EXERCISE_SCORE_UP: TrendingUp,
  EXERCISE_SCORE_DOWN: TrendingDown,
  CATEGORY_SCORE_UP: TrendingUp,
  CATEGORY_SCORE_DOWN: TrendingDown,
};
const HEADLINE_COLOR = {
  FIRST_PENTAGON: 'text-orange-600',
  CATEGORY_LEVEL_UP: 'text-orange-600',
  CATEGORY_LEVEL_DOWN: 'text-gray-500',
  EXERCISE_SCORE_UP: 'text-blue-600',
  EXERCISE_SCORE_DOWN: 'text-gray-500',
  CATEGORY_SCORE_UP: 'text-blue-600',
  CATEGORY_SCORE_DOWN: 'text-gray-500',
};

export const MemberInsightHistory = ({ memberId }) => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingId, setCreatingId] = useState(null);

  useEffect(() => {
    if (!memberId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/admin/members/${memberId}/insights`, {
          params: { limit: 100 },
        });
        setInsights(res.data.insights || []);
      } catch (e) {
        // 조용히 실패 - Insight 없는 회원은 정상 케이스
        setInsights([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [memberId]);

  const handleCreateOrOpenReport = async (ins) => {
    // 이미 생성됐으면 그냥 열기
    if (ins.linked_report_id) {
      navigate(`/admin/fit-reports/${ins.linked_report_id}`);
      return;
    }
    setCreatingId(ins._id);
    try {
      const res = await apiClient.post(`/admin/member-insights/${ins._id}/fit-report`);
      const reportId = res.data.report_id;
      toast.success(res.data.already_existed ? '이미 생성된 리포트를 엽니다' : 'Insight로부터 FIT REPORT를 생성했습니다');
      // Update local state to reflect linked_report_id
      setInsights((prev) =>
        prev.map((x) => (x._id === ins._id ? { ...x, linked_report_id: reportId } : x))
      );
      navigate(`/admin/fit-reports/${reportId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'FIT REPORT 생성 실패');
    } finally {
      setCreatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="border rounded-lg bg-white p-4 text-sm text-gray-500" data-testid="insight-history-loading">
        Insight History 불러오는 중...
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-white" data-testid="insight-history-section">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900">Insight History</h3>
        <p className="text-xs text-gray-500 mt-0.5">최신순 · 읽기 전용 · 각 인사이트에서 FIT REPORT를 생성할 수 있습니다</p>
      </div>

      {insights.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500" data-testid="insight-history-empty">
          아직 기록된 Insight가 없습니다.
        </div>
      ) : (
        <ul className="divide-y">
          {insights.map((ins) => {
            const Icon = HEADLINE_ICON[ins.headline_type] || Sparkles;
            const color = HEADLINE_COLOR[ins.headline_type] || 'text-gray-700';
            const hasReport = !!ins.linked_report_id;
            const busy = creatingId === ins._id;
            return (
              <li key={ins._id} className="px-4 py-3.5" data-testid={`insight-history-row-${ins._id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="text-xs text-gray-500">
                      {fmtKstDate(ins.measured_at || ins.created_at)}
                    </div>
                    <div className={`flex items-center gap-1.5 text-sm font-semibold ${color}`}>
                      <Icon className="h-4 w-4 shrink-0" />
                      <span data-testid={`insight-history-headline-${ins._id}`}>{ins.headline}</span>
                    </div>

                    {/* 관련 변화 (한 줄 중심) */}
                    <div className="text-xs text-gray-700 space-y-0.5 pl-5">
                      {(ins.all_category_changes || []).map((c, i) => {
                        const sig = sign(c.delta);
                        return (
                        <div key={`c${i}`}>
                          <span className="text-gray-500 mr-1.5">{koCat(c.category)}</span>
                          {c.prev} → {c.curr} <span className={`ml-1 ${sig.className}`}>{sig.text}</span>
                          {c.prev_level && c.curr_level && c.prev_level !== c.curr_level && (
                            <span className="ml-2 text-orange-700">
                              Level {c.prev_level.split(' ')[1]} → {c.curr_level.split(' ')[1]}
                            </span>
                          )}
                        </div>
                        );
                      })}
                      {(ins.all_exercise_changes || []).map((e, i) => {
                        const sig = sign(e.delta);
                        return (
                        <div key={`e${i}`}>
                          <span className="text-gray-500 mr-1.5">{koEx(e.exercise)}</span>
                          {rawMetrics(e.prev_raw_metrics) || e.prev_score} → {rawMetrics(e.curr_raw_metrics) || e.curr_score}
                          <span className={`ml-1 ${sig.className}`}>{sig.text}</span>
                        </div>
                        );
                      })}
                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant={hasReport ? 'outline' : 'default'}
                    disabled={busy}
                    onClick={() => handleCreateOrOpenReport(ins)}
                    className={
                      hasReport
                        ? 'text-green-700 border-green-300 hover:bg-green-50 shrink-0'
                        : 'bg-orange-600 hover:bg-orange-700 text-white shrink-0'
                    }
                    data-testid={hasReport ? `insight-report-open-${ins._id}` : `insight-report-create-${ins._id}`}
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    {busy ? '생성 중...' : hasReport ? 'FIT REPORT 보기' : 'FIT REPORT 생성'}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default MemberInsightHistory;
